import React, { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import DialogManageUser from "@/app/models/dialogs/DialogMangeUser";
import UniInput from "@/app/components/common/actions/inputs-b/UniInput";
import { UniDropdown } from "@/app/components/common/actions/inputs-b/UniDropdown";
import { ICON_BASE_PATH } from "@/app/utils/config";

import "../ThirdParty.css";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { RequestConfirmation } from "@/app/components/common/alert/RequestConfirmation";
import { useAdminContext } from "@/app/services/context/AdminContext";
import { useThirdPartySubFeatureUpdate } from "@/app/hooks/useAdminApiHooks";
import { FIELD_ICONS } from "../ThirdPartyFieldIcons";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage";

// --------------------------------------------------------------
// "Help" content shown in the info popup.
// --------------------------------------------------------------
const StripeHelpContent = () => (
  <div className="gx-p14-m gx-help-content">
    <p>
      To connect your Stripe account, you&apos;ll need a few credentials from
      your Stripe Dashboard:
    </p>

    <ol className="gx-help-list">
      <li>Log in to your Stripe Dashboard.</li>

      <li>
        Go to <strong>Developers → API Keys</strong>.
        <ol>
          <li>
            Copy your <strong>Secret Key</strong> (sk_test_...).
          </li>
          <li>
            Copy your <strong>Publishable Key</strong> (pk_test_...).
          </li>
        </ol>
      </li>

      <li>
        Next, go to <strong>Developers → Webhooks</strong>.
        <ol>
          <li>
            Add a new endpoint using the webhook URL provided in this platform.
          </li>
          <li>
            After creating it, open the webhook to reveal the{" "}
            <strong>Signing Secret</strong>.
          </li>
          <li>
            Copy the <strong>Webhook Secret</strong>.
          </li>
        </ol>
      </li>

      <li>
        Paste all the above values into the respective fields here and submit
        your request.
      </li>
    </ol>
  </div>
);

// --------------------------------------------------------------
// Static field configuration (unchanged).
// --------------------------------------------------------------
const DROPDOWN_OPTIONS: Record<string, string[]> = {};
const FIELD_TOOLTIPS: Record<string, string> = {};

const FIELD_PREFIX_CONFIG: Record<
  string,
  { prefixes: string[]; message: string }
> = {
  "Secret Key": {
    prefixes: ["sk_live_", "sk_test_"],
    message: "Secret Key must start with sk_live_ or sk_test_",
  },
  "Publishable Key": {
    prefixes: ["pk_live_", "pk_test_"],
    message: "Publishable Key must start with pk_live_ or pk_test_",
  },
  "Webhook Secret": {
    prefixes: ["whsec_"],
    message: "Webhook Secret must start with whsec_",
  },
};

const MASKED_FIELD_NAMES = new Set(["Secret Key", "Webhook Secret"]);

const getRequiredFieldMessage = (fieldName: string) =>
  `Please enter ${fieldName}`;

// Single source of truth for validating one field's value — required,
// prefix (FIELD_PREFIX_CONFIG) and max length (response's fieldLength).
// Returns "" when the value is valid.
const validateField = (
  field: { fieldName: string; fieldLength: number },
  value: string,
): string => {
  if (!value.trim()) {
    return getRequiredFieldMessage(field.fieldName);
  }

  const prefixConfig = FIELD_PREFIX_CONFIG[field.fieldName];
  if (
    prefixConfig &&
    !prefixConfig.prefixes.some((prefix) => value.startsWith(prefix))
  ) {
    return prefixConfig.message;
  }

  if (field.fieldLength > 0 && value.length > field.fieldLength) {
    return `${field.fieldName} must be at most ${field.fieldLength} characters`;
  }

  return "";
};

const Stripe = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // NEW: which fields the user has actually interacted with (typed in or
  // blurred). Errors are only *surfaced* for touched fields — but once a field
  // is touched its error persists on every keystroke until the value is
  // genuinely valid.
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [apiError, setApiError] = useState<{
    errorCode?: string | number | null;
    message?: string | null;
  }>({});

  const { selectedThirdPartySubFeature } = useAdminContext();
  const { mutate: updateThirdPartySubFeature } =
    useThirdPartySubFeatureUpdate();

  const isConnected = selectedThirdPartySubFeature?.recordState === "A";

  const fieldList =
    selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];

  // Seed field values from the backend-driven config list whenever the
  // selected sub-feature changes.
  useEffect(() => {
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const initial: Record<string, string> = {};
    list.forEach((field) => {
      initial[field.fieldName] = field.fieldValue ?? "";
    });
    setFieldValues(initial);
    setFieldErrors({});
    setTouchedFields({});
    setVisibleFields({});
    setApiError({});
  }, [selectedThirdPartySubFeature]);

  // NEW: recomputes and stores the error for one field. Keeps fieldErrors in
  // sync with the current value so an error can never be "lost" between
  // keystrokes.
  const setErrorFor = (name: string, value: string) => {
    const field = fieldList.find((f) => f.fieldName === name);
    const error = field ? validateField(field, value) : "";

    setFieldErrors((prev) => {
      if (!error) {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      }
      if (prev[name] === error) return prev;
      return { ...prev, [name]: error };
    });
  };

  // Updates a single field's value and re-validates it on every keystroke.
  // Typing marks the field as touched, so the message appears right away and
  // only clears once required + prefix + max length are all satisfied.
  const handleFieldChange = (name: string, val: string) => {
    setFieldValues((prev) => ({ ...prev, [name]: val }));
    setTouchedFields((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
    setErrorFor(name, val);
    // A new attempt invalidates the previous submit error.
    setApiError((prev) => (prev.errorCode || prev.message ? {} : prev));
  };

  // NEW: UniInput/UniDropdown call onValidate on blur — used as the "touched"
  // signal so tabbing through an empty field surfaces its required message
  // without waiting for submit.
  const handleFieldValidate = (name: string) => {
    setTouchedFields((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
    setErrorFor(name, fieldValues[name] ?? "");
  };

  const toggleFieldVisibility = (name: string) => {
    setVisibleFields((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Checks every rendered field with the same validateField logic used live.
  const validateRequiredFields = (): Record<string, string> => {
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const errors: Record<string, string> = {};

    list.forEach((field) => {
      const value = fieldValues[field.fieldName] ?? "";
      const error = validateField(field, value);
      if (error) {
        errors[field.fieldName] = error;
      }
    });

    return errors;
  };

  // NEW: the errors actually shown on the inputs — only for touched fields.
  const visibleFieldErrors = useMemo(() => {
    const shown: Record<string, string> = {};
    Object.entries(fieldErrors).forEach(([name, message]) => {
      if (touchedFields[name] && message) shown[name] = message;
    });
    return shown;
  }, [fieldErrors, touchedFields]);

  // NEW: detailed breakdown handed to RequiredMessage, in the same order the
  // fields are rendered so it reads top-to-bottom like the form.
  const errorDetails = useMemo(
    () =>
      fieldList
        .filter((field) => visibleFieldErrors[field.fieldName])
        .map((field) => ({
          fieldName: field.fieldName,
          message: visibleFieldErrors[field.fieldName],
        })),
    [fieldList, visibleFieldErrors],
  );

  const summaryMessage = apiError.errorCode
    ? null
    : apiError.message ||
      (errorDetails.length > 0
        ? "Please correct the highlighted fields before submitting."
        : null);

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (isConnected) return;

    if (!selectedThirdPartySubFeature) {
      console.error(
        "[Stripe] No selectedThirdPartySubFeature in context — cannot submit.",
      );
      return;
    }

    const errors = validateRequiredFields();
    if (Object.keys(errors).length > 0) {
      // NEW: mark every field touched on submit so all messages surface, both
      // inline and in the RequiredMessage summary.
      setFieldErrors(errors);
      setTouchedFields(
        fieldList.reduce<Record<string, boolean>>((acc, field) => {
          acc[field.fieldName] = true;
          return acc;
        }, {}),
      );
      setApiError({});
      return;
    }
    setFieldErrors({});

    const updatedCompConfigFeatureFeeList = (
      selectedThirdPartySubFeature.compConfigFeatureFeeList ?? []
    ).map((field) => ({
      smeConfFeatureFeeId: field.smeConfFeatureFeeId,
      smeFeatureFeeId: field.smeFeatureFeeId,
      groupSmeCompId: selectedThirdPartySubFeature.groupSmeCompId,
      subFeatureId: field.subFeatureId,
      subFeatureConfigId: field.subFeatureConfigId,
      cifId: selectedThirdPartySubFeature.cifId ?? null,
      fieldName: field.fieldName,
      fieldValue: fieldValues[field.fieldName] ?? field.fieldValue,
      redirectUrl: field.redirectUrl,
    }));

    const payload = {
      smeFeatureFeeId: selectedThirdPartySubFeature.smeFeatureFeeId,
      featureId: selectedThirdPartySubFeature.featureId,
      paramConfigFlag: selectedThirdPartySubFeature.paramConfigFlag,
      compConfigFeatureFeeList: updatedCompConfigFeatureFeeList,
    };

    try {
      setIsLoading(true);
      setApiError({});
      const responseData = await updateThirdPartySubFeature(payload);

      if (responseData?.data) {
        setShowSubmitted(true);
      } else {
        setApiError({
          errorCode: responseData?.errorCode ?? null,
          message:
            responseData?.errorMessage ||
            "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error(error);
      setApiError({
        errorCode: null,
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    navigate(-1);
  };

  const handleCloseSubmitted = () => {
    setShowSubmitted(false);
    navigate(-1);
  };

  return (
    <div className="gx-erp-container">
      {isLoading && <SubmitLoading />}

      {showSubmitted && (
        <RequestConfirmation
          actionType="submitted"
          onConfirm={() => handleCloseSubmitted()}
          onClose={handleCloseSubmitted}
          title="Your Stripe connection request has been submitted"
          message="Thank you for choosing GrowX. Our team will reach out to you at your registered contact number soon with further details regarding your request."
        />
      )}

      {showHelp && (
        <RequestConfirmation
          actionType="help"
          title="How to get this information"
          onConfirm={() => setShowHelp(false)}
          onClose={() => setShowHelp(false)}
          requiredContent={<StripeHelpContent />}
        />
      )}

      <DialogManageUser
        isOpen={isOpen}
        handleHeaderClose={handleCloseModal}
        handleFooterSubmit={handleSubmit}
        title={t("request-to-connect")}
        subtitle={t("stripe")}
        submit={t("submit_request")}
        cancel=""
        userRoll=""
        userStatus=""
        handleAcitiveUsers={() => console.log("Activate users")}
        hideFooter={isConnected}
      >
        <div className="gx-connect-container">
          <div className="gx-connect-header">
            <div className="gx-connect-header-text">
              <h1 className="gx-p18-sb">{t("connect-stripe")}</h1>
              <p className="gx-p12-m">
                Securely link your Stripe account to start accepting payments,
                manage transactions, and streamline your billing operations
                within the platform.
              </p>
            </div>

            <div className="gx-how-to-get" onClick={() => setShowHelp(true)}>
              <img
                src={`${ICON_BASE_PATH}interrogation-mark.svg`}
                alt="Info Icon"
              />
              <p className="gx-p14-sb">Help</p>
            </div>
          </div>

          <form className="gx-third-party-form" onSubmit={handleSubmit}>
            <div className="gx-third-party-form-grid">
              {fieldList.map((field) => {
                const commonProps = {
                  key: field.subFeatureConfigId,
                  fieldName: field.fieldName,
                  label: field.fieldName,
                  value: fieldValues[field.fieldName] ?? "",
                  // NEW: blur/touched signal instead of a console.log.
                  onValidate: (name: string) => handleFieldValidate(name),
                  // NEW: only touched fields show their message.
                  validationMessage: visibleFieldErrors[field.fieldName] ?? "",
                  tooltip: FIELD_TOOLTIPS[field.fieldName],
                  tooltipFloat: "down" as const,
                };

                if (field.fieldType === "DROPDOWN") {
                  return (
                    <UniDropdown
                      {...commonProps}
                      onChange={(val) =>
                        handleFieldChange(field.fieldName, val)
                      }
                      data={DROPDOWN_OPTIONS[field.fieldName] ?? []}
                      showStarLabel
                      iconSrc={FIELD_ICONS[field.fieldName] ?? "state.svg"}
                      minLength={3}
                      max={field.fieldLength > 0 ? field.fieldLength : 50}
                      disable={isConnected}
                    />
                  );
                }

                // Secret Key / Webhook Secret are masked on entry. UniInput
                // renders its own eye toggle when type === "password".
                const isMasked = MASKED_FIELD_NAMES.has(field.fieldName);
                const isVisible = visibleFields[field.fieldName] ?? false;

                return (
                  <div
                    key={field.subFeatureConfigId}
                    className={isMasked ? "gx-masked-field-wrap" : undefined}
                    onDoubleClick={
                      isMasked
                        ? () => toggleFieldVisibility(field.fieldName)
                        : undefined
                    }
                  >
                    <UniInput
                      {...commonProps}
                      onChange={(val) =>
                        handleFieldChange(field.fieldName, val)
                      }
                      iconFile={FIELD_ICONS[field.fieldName]}
                      minLength={20}
                      max={field.fieldLength > 0 ? field.fieldLength : 40}
                      disable={isConnected}
                      type={isMasked && !isVisible ? "password" : "text"}
                    />
                  </div>
                );
              })}
            </div>

            {/* Hidden submit lets DialogManageUser's footer button (which
                calls handleFooterSubmit) trigger native form submission. */}
            <button type="submit" className="gx-hidden-submit" />
          </form>

          {/* NEW: summary + per-field breakdown of exactly what's wrong. */}
          <div className="gx-stripe-required">
            <RequiredMessage
              errorCode={apiError.errorCode}
              message={summaryMessage}
              details={errorDetails}
            />
          </div>
        </div>
      </DialogManageUser>
    </div>
  );
};

export default Stripe;
